import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import type { DuplicateGroups, ReimbursementItem, Category, Receipt, Transaction } from '@/types'
import { AlertTriangle, Trash2, FileText, ImageIcon, Link2 } from 'lucide-react'
import { useState } from 'react'

interface DuplicateWarningProps {
  duplicates: DuplicateGroups
  categories: Category[]
}

interface ComparisonState {
  title: string
  left: Receipt | null
  leftLabel: string
  leftDetails: DetailRow[]
  right: Receipt | null
  rightLabel: string
  rightDetails: DetailRow[]
}

interface DetailRow {
  label: string
  value: string | null | undefined
}

function formatBytes(size: number | null | undefined) {
  if (!size) return null
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

function findCategory(categories: Category[], categoryId: number | null) {
  if (!categoryId) return null
  return categories.find((c) => c.id === categoryId)?.name || `Category #${categoryId}`
}

function receiptDetails(receipt: Receipt | null): DetailRow[] {
  if (!receipt) return []

  return [
    { label: 'File', value: receipt.filename },
    { label: 'Type', value: receipt.content_type },
    { label: 'Size', value: formatBytes(receipt.byte_size) },
    { label: 'Attached', value: receipt.created_at },
  ]
}

function itemDetails(item: ReimbursementItem, categories: Category[]): DetailRow[] {
  return [
    { label: 'Memo', value: item.memo || 'No description' },
    { label: 'Date', value: item.date },
    { label: 'Amount', value: item.amount?.formatted },
    { label: 'Category', value: findCategory(categories, item.category_id) },
    { label: 'Tax', value: item.has_tax ? 'Yes' : 'No' },
    { label: 'Uploaded', value: item.uploaded_at },
    ...receiptDetails(item.receipt),
  ]
}

function transactionDetails(transaction: Transaction, categories: Category[]): DetailRow[] {
  return [
    { label: 'Description', value: transaction.description },
    { label: 'Memo', value: transaction.memo },
    { label: 'Date', value: transaction.date },
    { label: 'Amount', value: transaction.amount?.formatted },
    { label: 'Category', value: findCategory(categories, transaction.category_id) },
    { label: 'Tax', value: transaction.has_tax ? 'Yes' : 'No' },
    { label: 'Reference', value: transaction.unique_reference },
    { label: 'Account', value: `#${transaction.account_id}` },
  ]
}

function ReceiptThumbnail({ item, onClick }: { item: ReimbursementItem; onClick?: () => void }) {
  const content = (
    <>
      {item.receipt?.content_type?.startsWith('image/') && item.receipt?.url ? (
        <img src={item.receipt.url} alt="" className="h-full w-full object-cover" />
      ) : item.receipt ? (
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
      ) : (
        <ImageIcon className="h-3.5 w-3.5 text-muted-foreground/40" />
      )}
    </>
  )

  if (onClick && item.receipt) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/50 transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title="Compare receipts"
      >
        {content}
      </button>
    )
  }

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/50">
      {content}
    </div>
  )
}

function TransactionReceiptButton({ receipt, onClick }: { receipt: Receipt; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/50 transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title={`Compare ${receipt.filename}`}
    >
      {receipt.content_type?.startsWith('image/') ? (
        <img src={receipt.url} alt="" className="h-full w-full object-cover" />
      ) : (
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </button>
  )
}

function ItemRow({
  item,
  categories,
  onReceiptClick,
}: {
  item: ReimbursementItem
  categories: Category[]
  onReceiptClick?: () => void
}) {
  const category = item.category_id ? categories.find((c) => c.id === item.category_id) : null

  return (
    <div className="flex items-center gap-3 py-2">
      <ReceiptThumbnail item={item} onClick={onReceiptClick} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-foreground truncate">
          {item.memo || 'No description'}
        </p>
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          {item.date && <span>{item.date}</span>}
          {category && <span>{category.name}</span>}
        </div>
      </div>
      <div className="shrink-0">
        {item.amount && <MoneyDisplay money={item.amount} size="sm" />}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
        onClick={() => router.delete(`/reimbursement_items/${item.id}`, { preserveScroll: true })}
        title="Discard"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

function DetailList({ rows }: { rows: DetailRow[] }) {
  return (
    <dl className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12px]">
      {rows.filter((row) => row.value).map((row) => (
        <div key={`${row.label}-${row.value}`} className="contents">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 truncate text-foreground">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ReceiptPanel({ label, receipt, details }: { label: string; receipt: Receipt | null; details: DetailRow[] }) {
  return (
    <div className="min-w-0 space-y-3">
      <div>
        <h3 className="text-[13px] font-medium text-foreground">{label}</h3>
        <DetailList rows={details} />
      </div>
      <div className="flex min-h-[360px] items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
        {receipt ? (
          receipt.content_type === 'application/pdf' ? (
            <iframe src={receipt.url} title={receipt.filename} className="h-[60vh] w-full" />
          ) : (
            <img src={receipt.url} alt={receipt.filename} className="max-h-[60vh] w-full object-contain" />
          )
        ) : (
          <div className="text-[13px] text-muted-foreground">No receipt attached</div>
        )}
      </div>
    </div>
  )
}

function ReceiptComparisonDialog({
  comparison,
  onClose,
}: {
  comparison: ComparisonState | null
  onClose: () => void
}) {
  return (
    <Dialog open={!!comparison} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-6xl max-h-[92vh] overflow-y-auto">
        {comparison && (
          <>
            <DialogHeader>
              <DialogTitle className="text-[15px]">{comparison.title}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 lg:grid-cols-2">
              <ReceiptPanel label={comparison.leftLabel} receipt={comparison.left} details={comparison.leftDetails} />
              <ReceiptPanel label={comparison.rightLabel} receipt={comparison.right} details={comparison.rightDetails} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function DuplicateWarning({ duplicates, categories }: DuplicateWarningProps) {
  const [comparison, setComparison] = useState<ComparisonState | null>(null)
  const hasItemDuplicates = duplicates.item_duplicates.length > 0
  const hasTransactionDuplicates = duplicates.transaction_duplicates.length > 0
  const hasLinkableTransactionMatches = duplicates.transaction_duplicates.some(
    ({ transaction }) => transaction.receipts.length === 0,
  )
  const hasReceiptDuplicates = hasItemDuplicates || duplicates.transaction_duplicates.some(
    ({ transaction }) => transaction.receipts.length > 0,
  )

  if (!hasItemDuplicates && !hasTransactionDuplicates) return null

  function compareItems(item: ReimbursementItem, otherItem: ReimbursementItem) {
    setComparison({
      title: 'Compare duplicate uploads',
      left: item.receipt,
      leftLabel: 'Selected upload',
      leftDetails: itemDetails(item, categories),
      right: otherItem.receipt,
      rightLabel: 'Other upload',
      rightDetails: itemDetails(otherItem, categories),
    })
  }

  function compareTransaction(item: ReimbursementItem, transaction: Transaction, receipt: Receipt | null) {
    setComparison({
      title: 'Compare duplicate receipts',
      left: item.receipt,
      leftLabel: 'Uploaded receipt',
      leftDetails: itemDetails(item, categories),
      right: receipt,
      rightLabel: 'Existing transaction receipt',
      rightDetails: [...transactionDetails(transaction, categories), ...receiptDetails(receipt)],
    })
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        {hasReceiptDuplicates ? (
          <AlertTriangle className="h-4 w-4 text-amber-500" />
        ) : (
          <Link2 className="h-4 w-4 text-primary" />
        )}
        <h2 className="text-[14px] font-medium text-foreground">
          {hasLinkableTransactionMatches && hasReceiptDuplicates
            ? 'Receipt Matches & Potential Duplicates'
            : hasLinkableTransactionMatches
              ? 'Receipt Matches'
              : 'Potential Duplicates'}
        </h2>
      </div>

      <div className="space-y-3">
        {/* Item duplicates */}
        {duplicates.item_duplicates.map((group, idx) => (
          <div key={idx} className="rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="px-4 py-2 border-b border-amber-500/10">
              <span className="text-[12px] font-medium text-amber-600">
                Possible duplicate uploads ({group.length} items with same amount)
              </span>
            </div>
            <div className="px-4 divide-y">
              {group.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  categories={categories}
                  onReceiptClick={() => {
                    const otherItem = group.find((candidate) => candidate.id !== item.id)
                    if (otherItem) compareItems(item, otherItem)
                  }}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Transaction duplicates */}
        {duplicates.transaction_duplicates.map(({ item, transaction }) => {
          const defaultReceipt = transaction.receipts[0] || null
          const canLink = transaction.receipts.length === 0

          return (
            <div
              key={item.id}
              className={canLink
                ? 'rounded-lg border border-primary/20 bg-primary/5'
                : 'rounded-lg border border-amber-500/20 bg-amber-500/5'}
            >
              <div className={canLink ? 'px-4 py-2 border-b border-primary/10' : 'px-4 py-2 border-b border-amber-500/10'}>
                <span className={canLink ? 'text-[12px] font-medium text-primary' : 'text-[12px] font-medium text-amber-600'}>
                  {canLink ? 'Existing transaction ready for this receipt' : 'Existing transaction with matching amount and date'}
                </span>
              </div>
              <div className="px-4 py-3">
                <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="flex min-w-0 gap-3">
                      <ReceiptThumbnail item={item} onClick={() => compareTransaction(item, transaction, defaultReceipt)} />
                      <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">Upload</Badge>
                          {item.amount && <MoneyDisplay money={item.amount} size="sm" />}
                        </div>
                        <p className="truncate text-[13px] font-medium text-foreground">
                          {item.memo || 'No description'}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
                          {item.date && <span>{item.date}</span>}
                          {findCategory(categories, item.category_id) && <span>{findCategory(categories, item.category_id)}</span>}
                          <span>Tax: {item.has_tax ? 'yes' : 'no'}</span>
                          {item.receipt?.filename && <span className="max-w-[220px] truncate">{item.receipt.filename}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex min-w-0 gap-3">
                      <div className="flex shrink-0 gap-1">
                        {transaction.receipts.length > 0 ? (
                          transaction.receipts.map((receipt) => (
                            <TransactionReceiptButton
                              key={receipt.id}
                              receipt={receipt}
                              onClick={() => compareTransaction(item, transaction, receipt)}
                            />
                          ))
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border bg-muted/50">
                            <FileText className="h-3.5 w-3.5 text-muted-foreground/40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">SBI</Badge>
                          <MoneyDisplay money={transaction.amount} size="sm" />
                        </div>
                        <p className="truncate text-[13px] font-medium text-foreground">
                          {transaction.description || transaction.memo || 'No description'}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
                          <span>{transaction.date}</span>
                          {transaction.memo && <span className="max-w-[220px] truncate">{transaction.memo}</span>}
                          {findCategory(categories, transaction.category_id) && <span>{findCategory(categories, transaction.category_id)}</span>}
                          <span>Tax: {transaction.has_tax ? 'yes' : 'no'}</span>
                          <span>Ref {transaction.unique_reference}</span>
                          <span>{transaction.receipts.length} receipt{transaction.receipts.length === 1 ? '' : 's'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5 justify-self-end">
                    {canLink && (
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => router.post(
                          `/reimbursement_items/${item.id}/link`,
                          { transaction_id: transaction.id },
                          { preserveScroll: true },
                        )}
                      >
                        <Link2 className="mr-1.5 h-3.5 w-3.5" />
                        Link
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
                      onClick={() => router.delete(`/reimbursement_items/${item.id}`, { preserveScroll: true })}
                      title="Discard"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <ReceiptComparisonDialog comparison={comparison} onClose={() => setComparison(null)} />
    </div>
  )
}
